import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <Box hidden={value !== index} mt={2}>{value === index && children}</Box>
);

const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
};

const defaultTo = () => new Date().toISOString().split('T')[0];

interface StatCard { label: string; value: string | number; }
const StatCards = ({ stats }: { stats: StatCard[] }) => (
  <Grid container spacing={2} mb={3}>
    {stats.map((s) => (
      <Grid item xs={12} sm={6} md={3} key={s.label}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h5" color="primary">{s.value}</Typography>
          <Typography variant="body2" color="textSecondary">{s.label}</Typography>
        </Paper>
      </Grid>
    ))}
  </Grid>
);

// ---- Appointments Report ----
const AppointmentsReport = ({ from, to }: { from: string; to: string }) => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [summary, setSummary] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(API_ENDPOINTS.REPORTS.APPOINTMENTS, { params: { from, to } });
      setData(res.data || res.appointments || res || []);
      setSummary(res.summary || {});
    } catch { setError('Failed to load appointments report.'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const total = typeof summary.total === 'number' ? summary.total : data.length;
  const completed = typeof summary.completed === 'number' ? summary.completed : '—';
  const cancelled = typeof summary.cancelled === 'number' ? summary.cancelled : '—';

  return (
    <>
      <StatCards stats={[
        { label: 'Total Appointments', value: total },
        { label: 'Completed', value: completed },
        { label: 'Cancelled', value: cancelled },
      ]} />
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Vet</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">No data</TableCell></TableRow>
            ) : data.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{String(row.date || row.appointment_date || row.appointment_time_start || '—')}</TableCell>
                <TableCell>{String(row.patient_name || row.pet_name || '—')}</TableCell>
                <TableCell>{String(row.vet_name || '—')}</TableCell>
                <TableCell>{String(row.type || row.appointment_type || '—')}</TableCell>
                <TableCell>{String(row.status || '—')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

// ---- Revenue Report ----
const RevenueReport = ({ from, to }: { from: string; to: string }) => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [summary, setSummary] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(API_ENDPOINTS.REPORTS.REVENUE, { params: { from, to } });
      setData(res.data || res.revenue || res || []);
      setSummary(res.summary || {});
    } catch { setError('Failed to load revenue report.'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const total = typeof summary.total_revenue === 'number'
    ? `$${summary.total_revenue.toFixed(2)}`
    : typeof summary.total === 'number' ? `$${summary.total.toFixed(2)}` : '—';

  return (
    <>
      <StatCards stats={[
        { label: 'Total Revenue', value: total },
        { label: 'Invoices', value: typeof summary.invoice_count === 'number' ? summary.invoice_count : data.length },
        { label: 'Avg per Invoice', value: typeof summary.average === 'number' ? `$${summary.average.toFixed(2)}` : '—' },
      ]} />
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Method</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">No data</TableCell></TableRow>
            ) : data.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{String(row.date || row.payment_date || '—')}</TableCell>
                <TableCell>{String(row.invoice_number || row.invoice_id || '—')}</TableCell>
                <TableCell>{String(row.client_name || '—')}</TableCell>
                <TableCell>${Number(row.amount || row.total_amount || 0).toFixed(2)}</TableCell>
                <TableCell>{String(row.payment_method || '—')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

// ---- Clients Report ----
const ClientsReport = ({ from, to }: { from: string; to: string }) => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [summary, setSummary] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(API_ENDPOINTS.REPORTS.CLIENTS, { params: { from, to } });
      setData(res.data || res.clients || res || []);
      setSummary(res.summary || {});
    } catch { setError('Failed to load clients report.'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <StatCards stats={[
        { label: 'New Clients', value: typeof summary.new_clients === 'number' ? summary.new_clients : data.length },
        { label: 'Total Active', value: typeof summary.total_active === 'number' ? summary.total_active : '—' },
      ]} />
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Registered</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center">No data</TableCell></TableRow>
            ) : data.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{String(row.name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—')}</TableCell>
                <TableCell>{String(row.email || '—')}</TableCell>
                <TableCell>{String(row.phone || row.phone_primary || '—')}</TableCell>
                <TableCell>{String(row.created_at || row.registered_at || '—')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

// ---- Patients Report ----
const PatientsReport = ({ from, to }: { from: string; to: string }) => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [summary, setSummary] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(API_ENDPOINTS.REPORTS.PATIENTS, { params: { from, to } });
      setData(res.data || res.patients || res || []);
      setSummary(res.summary || {});
    } catch { setError('Failed to load patients report.'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <StatCards stats={[
        { label: 'Total Patients', value: typeof summary.total === 'number' ? summary.total : data.length },
        { label: 'New this Period', value: typeof summary.new_patients === 'number' ? summary.new_patients : '—' },
      ]} />
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Species</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center">No data</TableCell></TableRow>
            ) : data.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{String(row.name || row.pet_name || '—')}</TableCell>
                <TableCell>{String(row.species || '—')}</TableCell>
                <TableCell>{String(row.owner_name || row.client_name || '—')}</TableCell>
                <TableCell>{String(row.status || '—')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

// ---- Outstanding Report ----
const OutstandingReport = ({ from, to }: { from: string; to: string }) => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [summary, setSummary] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'days_overdue' | 'total_amount'>('days_overdue');

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(API_ENDPOINTS.REPORTS.OUTSTANDING, { params: { from, to } });
      setData(res.data || res.invoices || res || []);
      setSummary(res.summary || {});
    } catch { setError('Failed to load outstanding report.'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const sorted = [...data].sort((a, b) => {
    const aVal = Number(a[sortField] || 0);
    const bVal = Number(b[sortField] || 0);
    return bVal - aVal;
  });

  const totalOutstanding = typeof summary.total_outstanding === 'number'
    ? `$${summary.total_outstanding.toFixed(2)}`
    : data.reduce((s, r) => s + Number(r.total_amount || r.amount || 0), 0).toFixed(2);

  return (
    <>
      <StatCards stats={[
        { label: 'Total Outstanding', value: `$${totalOutstanding}` },
        { label: 'Unpaid Invoices', value: typeof summary.count === 'number' ? summary.count : data.length },
        { label: 'Overdue (>30 days)', value: data.filter((r) => Number(r.days_overdue || 0) > 30).length },
      ]} />
      <Box display="flex" gap={1} mb={1}>
        <Button
          size="small"
          variant={sortField === 'days_overdue' ? 'contained' : 'outlined'}
          onClick={() => setSortField('days_overdue')}
        >
          Sort by Days Overdue
        </Button>
        <Button
          size="small"
          variant={sortField === 'total_amount' ? 'contained' : 'outlined'}
          onClick={() => setSortField('total_amount')}
        >
          Sort by Amount
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Invoice Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Days Overdue</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">No outstanding invoices</TableCell></TableRow>
            ) : sorted.map((row, i) => {
              const daysOverdue = Number(row.days_overdue || 0);
              return (
                <TableRow key={i} sx={{ bgcolor: daysOverdue > 30 ? 'error.50' : 'inherit' }}>
                  <TableCell>{String(row.invoice_number || row.invoice_id || '—')}</TableCell>
                  <TableCell>{String(row.client_name || '—')}</TableCell>
                  <TableCell>{String(row.invoice_date || row.date || '—')}</TableCell>
                  <TableCell>${Number(row.total_amount || row.amount || 0).toFixed(2)}</TableCell>
                  <TableCell sx={{ color: daysOverdue > 30 ? 'error.main' : 'inherit', fontWeight: daysOverdue > 30 ? 700 : 400 }}>
                    {daysOverdue > 0 ? daysOverdue : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

// ---- Main Reports Page ----
const ReportsPage = () => {
  const [tab, setTab] = useState(0);
  const [dateFrom, setDateFrom] = useState(defaultFrom());
  const [dateTo, setDateTo] = useState(defaultTo());
  const [appliedFrom, setAppliedFrom] = useState(defaultFrom());
  const [appliedTo, setAppliedTo] = useState(defaultTo());

  const applyFilter = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Reports</Typography>

      <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
        <TextField
          label="From"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <TextField
          label="To"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
        <Button variant="contained" onClick={applyFilter}>Apply</Button>
      </Box>

      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Appointments" />
          <Tab label="Revenue" />
          <Tab label="Clients" />
          <Tab label="Patients" />
          <Tab label="Outstanding" />
        </Tabs>
        <Box p={2}>
          <TabPanel value={tab} index={0}><AppointmentsReport from={appliedFrom} to={appliedTo} /></TabPanel>
          <TabPanel value={tab} index={1}><RevenueReport from={appliedFrom} to={appliedTo} /></TabPanel>
          <TabPanel value={tab} index={2}><ClientsReport from={appliedFrom} to={appliedTo} /></TabPanel>
          <TabPanel value={tab} index={3}><PatientsReport from={appliedFrom} to={appliedTo} /></TabPanel>
          <TabPanel value={tab} index={4}><OutstandingReport from={appliedFrom} to={appliedTo} /></TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportsPage;
